// @flow

import * as React from 'react'
import sortBy from 'lodash/sortBy'

export type PreparedDesignPage = {
  key: string,
  deletable?: boolean,
  label: string,
  labelHelpIndicator?: string,
  sublabel?: string,
  section: string,
  shopPath: string,
  designPage: Object,
  designPageSettings?: React.ComponentType<any>,
}

export const checkIfIsInWebshopEditorIframe = (): boolean => {
  // Just checking window.self !== window.parent is not enough, because that
  // will be true if shop is in any iframe, not just in our webshop editor
  return (
    window.self !== window.parent &&
    window.parent.location.pathname.match(/^\/shop\/[0-9]+\/editor/) !== null
  )
}

export const useWebshopEditorIframeMessageListener = (
  onWebshopEditorIframeMessageReceived: Function
) => {
  const isInWebshopEditorIframe = checkIfIsInWebshopEditorIframe()

  const onMessageEventReceived = React.useCallback(
    (event: MessageEvent) => {
      const message: Object = event.data

      onWebshopEditorIframeMessageReceived(message)
    },
    [onWebshopEditorIframeMessageReceived]
  )

  React.useEffect(() => {
    if (!isInWebshopEditorIframe) {
      return
    }

    window.addEventListener('message', onMessageEventReceived)
    return () => window.removeEventListener('message', onMessageEventReceived)
  }, [isInWebshopEditorIframe, onMessageEventReceived])
}

export const WEBSHOP_EDITOR_IFRAME_GRID_ITEMS_CHANGED_MESSAGE_TYPE =
  'traede.shopV2.webshopEditor.gridItemsChanged'

export type WebshopEditorIframeGridItemsChangedMessage = {
  type: typeof WEBSHOP_EDITOR_IFRAME_GRID_ITEMS_CHANGED_MESSAGE_TYPE,
  payload: {
    designPageId: string,
    gridItems: Array<Object>,
  },
}

export const createWebshopEditorIframeGridItemsChangedMessage = (
  designPageId: string,
  gridItems: Array<Object>
): WebshopEditorIframeGridItemsChangedMessage => ({
  type: WEBSHOP_EDITOR_IFRAME_GRID_ITEMS_CHANGED_MESSAGE_TYPE,
  payload: {
    designPageId,
    gridItems,
  },
})

export const WEBSHOP_EDITOR_IFRAME_DESIGN_PAGE_UPDATED_MESSAGE_TYPE =
  'traede.shopV2.webshopEditor.designPageUpdated'

export type WebshopEditorIframeDesignPageUpdatedMessage = {
  type: typeof WEBSHOP_EDITOR_IFRAME_DESIGN_PAGE_UPDATED_MESSAGE_TYPE,
  payload: {
    designPageId: string,
  },
}

export const createWebshopEditorIframeDesignPageUpdatedMessage = (
  designPageId: string
): WebshopEditorIframeDesignPageUpdatedMessage => ({
  type: WEBSHOP_EDITOR_IFRAME_DESIGN_PAGE_UPDATED_MESSAGE_TYPE,
  payload: {
    designPageId,
  },
})

export const WEBSHOP_EDITOR_IFRAME_GRID_ITEM_EDITING_STARTED_MESSAGE_TYPE =
  'traede.shopV2.webshopEditor.gridItemEditingStarted'

export type WebshopEditorIframeGridItemEditingStartedMessage = {
  type: typeof WEBSHOP_EDITOR_IFRAME_GRID_ITEM_EDITING_STARTED_MESSAGE_TYPE,
  payload: {
    gridItemId: string,
  },
}

export const createWebshopEditorIframeGridItemEditingStartedMessage = (
  gridItemId: string
): WebshopEditorIframeGridItemEditingStartedMessage => ({
  type: WEBSHOP_EDITOR_IFRAME_GRID_ITEM_EDITING_STARTED_MESSAGE_TYPE,
  payload: {
    gridItemId,
  },
})

export const WEBSHOP_EDITOR_IFRAME_GRID_ITEM_EDITING_ENDED_MESSAGE_TYPE =
  'traede.shopV2.webshopEditor.gridItemEditingEnded'

export type WebshopEditorIframeGridItemEditingEndedMessage = {
  type: typeof WEBSHOP_EDITOR_IFRAME_GRID_ITEM_EDITING_ENDED_MESSAGE_TYPE,
}

export const createWebshopEditorIframeGridItemEditingEndedMessage = (
  gridItemId: string
): WebshopEditorIframeGridItemEditingEndedMessage => ({
  type: WEBSHOP_EDITOR_IFRAME_GRID_ITEM_EDITING_ENDED_MESSAGE_TYPE,
})

export const mapTreeToGridItemsList = tree => {
  const parentByItemId = {}
  for (let item of Object.values(tree.items)) {
    for (let childId of item.children) {
      parentByItemId[childId] = item.id
    }
  }

  const gridItemsList = []
  for (let item of Object.values(tree.items)) {
    if (item.id == tree.rootId) {
      continue
    }

    const parentId = parentByItemId[item.id]
    const parent = tree.items[parentId]
    const parentSort = parent.children.indexOf(item.id)

    gridItemsList.push({
      ...item.data,
      id: item.id,
      parent_grid_item_id: parentId == tree.rootId ? null : parentId,
      parent_sort: parentSort,
    })
  }

  return gridItemsList
}

export const addChildrenToGridItems = (
  inputGridItems,
  parentGridItemId = null
) => {
  const mappedGridItems = []

  const levelGridItems = inputGridItems.filter(
    gridItem => gridItem.parent_grid_item_id == parentGridItemId
  )

  for (let gridItem of levelGridItems) {
    mappedGridItems.push({
      ...gridItem,
      children: addChildrenToGridItems(inputGridItems, gridItem.id),
    })
  }

  return sortBy(mappedGridItems, 'parent_sort')
}

export const removeIdsFromNewWidgets = gridItems => {
  const updated = []

  for (let gridItem of gridItems) {
    const copy = { ...gridItem }

    if (copy.__new) {
      delete copy.__new
      delete copy.id
    }

    if (copy.children) {
      copy.children = removeIdsFromNewWidgets(copy.children)
    }

    updated.push(copy)
  }

  return updated
}
